import { BranchTag } from "@/components/BranchTag";
import { raisedMeta } from "@/lib/branchInfo";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Send, ArrowRightCircle, ChevronDown, ChevronRight, CalendarDays, FileText, MoreVertical, Pencil } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ApprovalReviewButton } from "@/components/procurement/ApprovalReview";
import { ItemPicker, loadCatalogue } from "@/components/procurement/ItemPicker";
import { WbsItemSelect } from "@/components/procurement/WbsItemSelect";
import { ProjectFilter, useProjectFilter } from "@/components/procurement/ProjectFilter";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageBreadcrumb } from "@/components/PageBreadcrumb";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";
import { CompanyField } from "@/components/CompanyField";

import { confirmAction } from "@/lib/dialogs";
import { DraftProcurementEditor } from "@/components/procurement/DraftProcurementEditor";
// indents-design: expandable cards - number, date / time / item count, "From requisition", status, View, menu
const PROCUREMENT_WRITE_ROLES = ["ORG_ADMIN", "PROCUREMENT"];
const INDENT_PROJECT_ROLES = ["PROJECT_MANAGER", "ENGINEERING", "SITE"];
const EMPTY_ITEM = { description: "", quantity: "", estimated_rate: "", wbs_item_id: "" };
const DELETABLE = ["DRAFT", "REJECTED", "CANCELLED"];
const timeOf = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }).toLowerCase() : "";
};

export default function PurchaseIndents() {
  const pf = useProjectFilter();
  const { user } = useAuth();
  const canWrite = PROCUREMENT_WRITE_ROLES.includes(user?.role);
  const [indentProjectIds, setIndentProjectIds] = useState([]);
  const { companies, singleCompany } = useCompany() || {};
  const [indents, setIndents] = useState([]);
  const [requisitions, setRequisitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState({});
  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, prRes] = await Promise.all([api.get("/procurement/indents"), api.get("/procurement/requisitions").catch(() => ({ data: [] }))]);
      const sorted = [...data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setIndents(sorted);
      setRequisitions(prRes.data || []);
      // the newest indent opens by default so its items are visible straight away
      setExpanded((e) => (Object.keys(e).length || !sorted.length ? e : { [sorted[0].id]: true }));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (canWrite || user?.role !== "MEMBER") return;
    api.get("/procurement/indents/creation-projects")
      .then(({ data }) => setIndentProjectIds(data || []))
      .catch((err) => toast.error(getErrorMessage(err)));
  }, [canWrite, user?.role]);

  const canRaiseIndent = canWrite || pf.projects.some((project) => indentProjectIds.includes(project.id));

  const requisitionOf = (indent) => requisitions.find((r) => r.indent_id === indent.id);

  const submit = async (indent) => {
    setBusyId(indent.id);
    try {
      const { data } = await api.post(`/procurement/indents/${indent.id}/submit`);
      toast.success(data?.status === "CONVERTED"
        ? "Indent approved — a Purchase Requisition was created automatically"
        : "Indent submitted — once approved, its Purchase Requisition is created automatically");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const convert = async (indent) => {
    setBusyId(indent.id);
    try {
      await api.post(`/procurement/indents/${indent.id}/convert-to-requisition`, { requisition_date: new Date().toISOString().slice(0, 10) });
      toast.success("Converted to purchase requisition");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (indent) => {
    if (!await confirmAction(`Mark Indent ${indent.number} as deleted? It will remain in the procurement history.`)) return;
    try {
      await api.delete(`/procurement/indents/${indent.id}`);
      toast.success(`Indent ${indent.number} marked deleted`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-5" data-testid="purchase-indents-page">
      <div className="space-y-2">
        <PageBreadcrumb crumbs={[{ label: "Operations" }, { label: "Purchase Indents" }]} />
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight text-foreground">Purchase Indents</h1>
            <p className="mt-1 max-w-3xl text-sm text-muted-foreground">Internal requests to purchase — the first step of the procure-to-pay chain.</p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2"><ProjectFilter filter={pf} />{canRaiseIndent && <CreateIndentDialog companies={companies} pfProjects={pf.projects} eligibleProjectIds={indentProjectIds} canWrite={canWrite} onCreated={load} />}</div>
        </div>
      </div>
      {!loading && indents.length === 0 ? (
        <EmptyState title="No purchase indents yet" testId="indents-empty-state" />
      ) : (
        <div className="space-y-3">
          {pf.apply(indents, (r) => r.project_id).map((indent) => {
            const open = !!expanded[indent.id];
            const pr = requisitionOf(indent);
            const count = indent.items.length;
            const canDelete = canWrite && DELETABLE.includes(indent.status);
            const hasMenu = canWrite && (indent.status === "DRAFT" || indent.status === "APPROVED" || canDelete);
            const prDeletedByRole = pr?.deleted_by_role === "ORG_ADMIN"
              ? "Admin"
              : pr?.deleted_by_role === "PROCUREMENT"
                ? "Procurement Officer"
                : null;
            return (
              <div key={indent.id} className="rounded-xl border border-border bg-card shadow-sm" data-testid={`indent-card-${indent.number}`}>
                <div className="flex flex-wrap items-start justify-between gap-3 p-4">
                  <button type="button" className="flex min-w-0 items-start gap-3 text-left" onClick={() => toggleExpand(indent.id)} aria-expanded={open} data-testid={`indent-toggle-${indent.number}`}>
                    {open ? <ChevronDown className="mt-1 h-5 w-5 shrink-0 text-foreground" /> : <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-foreground" />}
                    <span className="min-w-0">
                      <span className="block text-lg font-bold leading-tight text-foreground">{indent.number}</span><BranchTag row={indent} />
                      <span className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                        <CalendarDays className="h-3.5 w-3.5" /> {indent.indent_date}
                        {timeOf(indent.created_at) && <><span aria-hidden>•</span>{timeOf(indent.created_at)}</>}
                        <span aria-hidden>•</span>{count} item{count === 1 ? "" : "s"}
                        {raisedMeta(indent, singleCompany).map((m) => <span key={m} className="inline-flex items-center gap-x-2" data-testid="indent-raised-meta"><span aria-hidden>•</span>{m}</span>)}
                      </span>
                    </span>
                  </button>
                  <div className="flex flex-wrap items-center justify-end gap-3" onClick={(e) => e.stopPropagation()}>
                    <StatusPill value={indent.status} />
                    {canWrite && indent.status === "DRAFT" && (
                      <Button size="sm" variant="outline" disabled={busyId === indent.id} onClick={() => submit(indent)} data-testid={`submit-indent-button-${indent.number}`}>
                        <Send className="h-3.5 w-3.5" /> Submit
                      </Button>
                    )}
                    <ApprovalReviewButton docType="PI" docId={indent.id} onChanged={load} icon={FileText} className="h-10 rounded-lg px-6 font-semibold" />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="h-9 w-9" title="More" data-testid={`indent-menu-${indent.number}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="min-w-[180px]">
                        {canWrite && indent.status === "DRAFT" && <DropdownMenuItem onSelect={() => setEditing(indent)} data-testid={`edit-${indent.number}`}><Pencil className="h-4 w-4" /> Edit draft</DropdownMenuItem>}
                        {canWrite && indent.status === "APPROVED" && <DropdownMenuItem disabled={busyId === indent.id} onSelect={() => convert(indent)} data-testid={`convert-indent-button-${indent.number}`}><ArrowRightCircle className="h-4 w-4" /> Convert to PR</DropdownMenuItem>}
                        {canDelete && <DropdownMenuItem className="text-rose-600 focus:text-rose-600" onSelect={() => remove(indent)} data-testid={`delete-${indent.number}`}><Trash2 className="h-4 w-4" /> Mark deleted</DropdownMenuItem>}
                        {!hasMenu && <DropdownMenuItem disabled>No other actions</DropdownMenuItem>}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                {pr?.status === "DELETED" ? (
                  <p className="-mt-2 px-4 pb-3 pl-12 text-sm text-rose-600 dark:text-rose-400" data-testid={`indent-from-pr-${indent.number}`}>
                    Purchase requisition <Link to="/erp/operations/purchase-requisitions" className="hover:underline">{pr.number}</Link> was deleted{pr.deleted_by_name ? ` by ${pr.deleted_by_name}` : ""}{prDeletedByRole ? ` (${prDeletedByRole})` : ""}.
                  </p>
                ) : pr && <p className="-mt-2 px-4 pb-3 pl-12 text-sm text-sky-600 dark:text-sky-400" data-testid={`indent-from-pr-${indent.number}`}>From requisition <Link to="/erp/operations/purchase-requisitions" className="hover:underline">{pr.number}</Link></p>}
                {open && (
                  <div className="px-4 pb-4">
                    <div className="rounded-xl border border-border/70 bg-secondary/40 px-5 py-4" data-testid={`indent-items-${indent.number}`}>
                      <div className="grid grid-cols-[2fr_1fr_1fr] gap-4 text-xs font-semibold text-muted-foreground"><span>Description</span><span>Quantity</span><span>Est. Rate</span></div>
                      <div className="mt-2 space-y-2">
                        {indent.items.map((item) => (
                          <div key={item.id} className="grid grid-cols-[2fr_1fr_1fr] gap-4 text-sm text-foreground">
                            <span className="min-w-0 break-words">{item.description}</span>
                            <span className="tabular-nums">{Number(item.quantity)}</span>
                            <span className="tabular-nums">{item.estimated_rate ? `₹${Number(item.estimated_rate).toFixed(2)}` : "—"}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <DraftProcurementEditor phase="indent" document={editing} projects={pf.projects} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
}

function CreateIndentDialog({ companies, pfProjects, eligibleProjectIds, canWrite, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", indent_date: "", remarks: "" });
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const updateItem = (idx, field, value) => setItems(items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  // Picking a catalogue item fills description, unit and price (price can still be changed).
  const pickItem = (idx, item) => setItems(items.map((it, i) => (i === idx ? {
    ...it, item_id: item.id, description: item.name, uom_id: item.uom_id || it.uom_id || null, estimated_rate: Number(item.standard_rate) > 0 ? String(item.standard_rate) : it.estimated_rate,
  } : it)));

  const submit = async (e) => {
    e.preventDefault();
    if (!canWrite && !eligibleProjectIds.includes(form.project_id)) {
      toast.error("Select a project where you have an Engineering, Site or Project Manager role");
      return;
    }
    setSaving(true);
    try {
      await api.post("/procurement/indents", {
        ...form, project_id: form.project_id || null,
        items: items.map((it) => ({ ...it, estimated_rate: it.estimated_rate || null, wbs_item_id: it.wbs_item_id || null })),
      });
      const typed = items.filter((it) => !it.item_id && it.description.trim()).length;
      toast.success(typed ? "Purchase indent created — new item names were added to Items & Prices" : "Purchase indent created");
      if (typed) loadCatalogue(true);   // the pickers must see the items just added
      setForm({ company_id: "", indent_date: "", remarks: "" });
      setItems([{ ...EMPTY_ITEM }]);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-indent-button" data-tour="indent-new"><Plus className="h-4 w-4" /> New Indent</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Purchase Indent</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-indent-form">
          <div>
            <Label>Project <span className="font-normal text-muted-foreground">(the site this material is for)</span></Label>
            <Select required={!canWrite} value={form.project_id || ""} onValueChange={(v) => setForm({ ...form, project_id: v })}>
              <SelectTrigger className="mt-1.5" data-testid="indent-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
              <SelectContent>{(pfProjects || []).filter((p) => canWrite || eligibleProjectIds.includes(p.id)).map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <CompanyField label="Company" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} testId="indent-company-select" />
            <div><Label>Date</Label><Input required type="date" data-testid="indent-date-input" value={form.indent_date} onChange={set("indent_date")} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>Items</Label>
            <p className="mt-1 text-[11px] text-muted-foreground">Link a line to a WBS material so its RFQ/order/delivery dates fill in automatically as procurement moves along.</p>
            <div className="mt-1.5 space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="space-y-1 rounded-md border border-transparent p-1 hover:border-border" data-testid={`indent-item-row-${idx}`}>
                  <div className="flex items-center gap-2">
                    <ItemPicker required value={it.description} onChange={(v) => updateItem(idx, "description", v)} onPick={(item) => pickItem(idx, item)} data-testid={`indent-item-desc-input-${idx}`} />
                    <Input required type="number" step="0.001" placeholder="Qty" className="w-24" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} data-testid={`indent-item-qty-input-${idx}`} />
                    <Input type="number" step="0.01" placeholder="Est. rate" className="w-28" value={it.estimated_rate} onChange={(e) => updateItem(idx, "estimated_rate", e.target.value)} data-testid={`indent-item-rate-input-${idx}`} />
                    {items.length > 1 && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => setItems(items.filter((_, i) => i !== idx))} data-testid={`remove-indent-item-button-${idx}`}>
                        <Trash2 className="h-3.5 w-3.5 text-rose-400" />
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center gap-2 pl-0.5">
                    <span className="w-20 shrink-0 text-[11px] text-muted-foreground">WBS activity</span>
                    <div className="max-w-xs flex-1"><WbsItemSelect projectId={form.project_id} value={it.wbs_item_id} onChange={(v) => updateItem(idx, "wbs_item_id", v)} testId={`indent-item-wbs-select-${idx}`} /></div>
                  </div>
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => setItems([...items, { ...EMPTY_ITEM }])} data-testid="add-indent-item-button">
              <Plus className="h-3.5 w-3.5" /> Add item
            </Button>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-indent-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
